import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutsideClick, useMobileScrollLock } from '@/common/hooks';

function ScrollColumn({ options, value, onChange }) {
  const ref = useRef(null);
  const ITEM_HEIGHT = 36;
  const VISIBLE_HEIGHT = 150;

  useEffect(() => {
    if (ref.current) {
      const idx = options.findIndex((o) => o.value === value);
      if (idx >= 0) {
        ref.current.scrollTop = idx * ITEM_HEIGHT;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only set initial scroll position

  const handleScroll = (e) => {
    if (!e.target) return;
    const idx = Math.round(e.target.scrollTop / ITEM_HEIGHT);
    const opt = options[idx];
    if (opt && opt.value !== value) {
      onChange(opt.value);
    }
  };

  return (
    <div
      ref={ref}
      onScroll={handleScroll}
      className="h-[150px] w-12 overflow-y-auto no-scrollbar relative z-10"
      style={{ scrollSnapType: 'y mandatory' }}
    >
      <div style={{ height: (VISIBLE_HEIGHT - ITEM_HEIGHT) / 2 }} />
      {options.map((opt) => (
        <div
          key={opt.value}
          onClick={() => {
            onChange(opt.value);
            if (ref.current) {
              const idx = options.findIndex((o) => o.value === opt.value);
              ref.current.scrollTo({
                top: idx * ITEM_HEIGHT,
                behavior: 'smooth',
              });
            }
          }}
          className={`flex items-center justify-center text-[15px] cursor-pointer transition-all ${
            opt.value === value
              ? 'font-semibold text-[#1F2241]'
              : 'font-medium text-[#868686] opacity-40 hover:opacity-70'
          }`}
          style={{ height: ITEM_HEIGHT, scrollSnapAlign: 'center' }}
        >
          {opt.label}
        </div>
      ))}
      <div style={{ height: (VISIBLE_HEIGHT - ITEM_HEIGHT) / 2 }} />
    </div>
  );
}

export function TimePickerInput({
  value,
  onChange,
  disabled = false,
  placeholder = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverCoords, setPopoverCoords] = useState(null);
  const containerRef = useRef(null);
  const popupRef = useRef(null);

  useMobileScrollLock(isOpen);

  // Draft state for the unified picker
  const [draftHour, setDraftHour] = useState('12');
  const [draftMinute, setDraftMinute] = useState('00');
  const [draftAmpm, setDraftAmpm] = useState('PM');

  // Initialize draft state when opening
  useEffect(() => {
    if (isOpen) {
      const [hourStr, minuteStr] = (value || '09:00').split(':');
      let h24 = parseInt(hourStr, 10);
      const isPM = h24 >= 12;
      let h12 = h24 % 12;
      h12 = h12 ? h12 : 12;

      setDraftHour(h12.toString().padStart(2, '0'));
      setDraftMinute(minuteStr);
      setDraftAmpm(isPM ? 'PM' : 'AM');
    }
  }, [isOpen, value]);

  useOutsideClick(
    [containerRef, popupRef],
    () => {
      setIsOpen(false);
    },
    isOpen,
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    function updatePosition() {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const pad = 8;
      const popupWidth = Math.min(260, window.innerWidth - pad * 2);
      const popupHeight = popupRef.current?.offsetHeight || 280;

      // Prefer left-align to trigger; if it overflows, right-align to trigger; then clamp
      let left = rect.left;
      if (left + popupWidth > window.innerWidth - pad) {
        left = rect.right - popupWidth;
      }
      left = Math.min(
        Math.max(left, pad),
        window.innerWidth - popupWidth - pad,
      );

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placeAbove =
        spaceBelow < popupHeight + pad && spaceAbove > spaceBelow;

      if (placeAbove) {
        setPopoverCoords({
          top: 'auto',
          bottom: window.innerHeight - rect.top + 4,
          left,
          width: popupWidth,
        });
      } else {
        setPopoverCoords({
          top: Math.min(
            rect.bottom + 4,
            window.innerHeight - popupHeight - pad,
          ),
          bottom: 'auto',
          left,
          width: popupWidth,
        });
      }
    }

    updatePosition();
    // Re-measure after paint so popupHeight is accurate
    const raf = requestAnimationFrame(updatePosition);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const [hourStr, minuteStr] = (value || '09:00').split(':');
  let h24 = parseInt(hourStr, 10);
  const isPM = h24 >= 12;
  let h12 = h24 % 12;
  h12 = h12 ? h12 : 12;

  const displayHour = h12.toString().padStart(2, '0');
  const ampm = isPM ? 'PM' : 'AM';
  const displayTime = `${displayHour}:${minuteStr} ${ampm}`;

  const hourOptions = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString().padStart(2, '0'),
    label: (i + 1).toString().padStart(2, '0'),
  }));

  const minuteOptions = Array.from({ length: 60 }, (_, i) => {
    const str = i.toString().padStart(2, '0');
    return {
      value: str,
      label: str,
    };
  });

  const ampmOptions = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' },
  ];

  const handleSave = () => {
    let newH12 = parseInt(draftHour, 10);
    let newH24 = newH12;
    if (draftAmpm === 'PM' && newH12 < 12) newH24 += 12;
    if (draftAmpm === 'AM' && newH12 === 12) newH24 = 0;

    onChange(`${newH24.toString().padStart(2, '0')}:${draftMinute}`);
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-11 flex items-center justify-between px-4 rounded-xl bg-white border ${
          disabled
            ? 'opacity-60 cursor-not-allowed bg-gray-50 border-[#EBF4EA]'
            : isOpen
              ? 'border-[#50AB3D] ring-2 ring-[#50AB3D]/15 shadow-sm'
              : 'border-[#EBF4EA] hover:border-[#50AB3D] shadow-sm'
        } text-[#1F2241] text-sm font-semibold focus:outline-none transition-all`}
      >
        <span className={value ? '' : 'text-[#868686] font-medium'}>
          {value ? displayTime : placeholder || displayTime}
        </span>
        <svg
          className={`w-4 h-4 ml-1 flex-shrink-0 transition-transform text-[#50AB3D] ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>

      {isOpen &&
        popoverCoords &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998]"
              style={{ touchAction: 'none' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
              }}
            />
            <div
              ref={popupRef}
              data-overlay-container="true"
              className="fixed z-[9999] bg-white border border-[#EBF4EA] rounded-2xl shadow-2xl p-4 flex flex-col max-w-[calc(100vw-16px)] animate-in fade-in zoom-in-95 duration-150 origin-top"
              style={{ ...popoverCoords, overscrollBehavior: 'contain' }}
            >
              <div className="text-center font-semibold text-[#1F2241] mb-3">
                Select time
              </div>

              <div className="relative flex items-center justify-center h-[150px] mb-4 overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[36px] bg-[#F5F5F5] border border-[#E8E8E8] rounded-xl pointer-events-none z-0" />

                <ScrollColumn
                  options={hourOptions}
                  value={draftHour}
                  onChange={setDraftHour}
                />
                <span className="font-bold text-[#1F2241] pb-1 z-10 mx-1">
                  :
                </span>
                <ScrollColumn
                  options={minuteOptions}
                  value={draftMinute}
                  onChange={setDraftMinute}
                />
                <div className="w-2" />
                <ScrollColumn
                  options={ampmOptions}
                  value={draftAmpm}
                  onChange={setDraftAmpm}
                />
              </div>

              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 h-10 text-sm font-semibold text-[#868686] hover:text-[#1F2241] transition-colors rounded-xl border border-transparent hover:border-[#E8E8E8]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="flex-1 h-10 text-sm font-semibold text-white bg-primary rounded-xl shadow-sm hover:bg-primary/90 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
